import { KEY } from '@constants/key';
import { useLocalization } from '@lib/localization';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useLoginMutation } from '@store/api/authEndpoints';
import { gray } from '@theme/primitives';
import React, { type ChangeEvent, useEffect, useRef, useState } from 'react';

const SignInCard = () => {
  const [usernameError, setUsernameError] = React.useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [isRememberMeChecked, setRememberMeChecked] = useState(false);
  const [usernameFieldValue, setUsernameFieldValue] = useState('');
  const passwordTextFieldRef = useRef<HTMLInputElement>(null);

  const [login, { isLoading }] = useLoginMutation();

  const { t } = useLocalization();

  useEffect(() => {
    const username = localStorage.getItem(KEY.USERNAME);
    setRememberMeChecked(!!username);

    if (username) {
      setUsernameFieldValue(username);
      passwordTextFieldRef.current?.focus();
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateInputs()) {
      return;
    }
    try {
      const formData = new FormData(event.currentTarget);
      const username = formData.get(KEY.USERNAME)?.toString() ?? '';
      const password = formData.get(KEY.PASSWORD)?.toString() ?? '';
      await login({ username, password }).unwrap();
      if (isRememberMeChecked) localStorage.setItem(KEY.USERNAME, username);
    } catch (_) {
      setPasswordError(true);
      setPasswordErrorMessage('Invalid username or password');
    }
  };

  const validateInputs = (): boolean => {
    const username = (document.getElementById(KEY.USERNAME) as HTMLInputElement)?.value;
    const password = (document.getElementById(KEY.PASSWORD) as HTMLInputElement)?.value;
    let isValid = true;

    if (!username || username.length < 3) {
      setUsernameError(true);
      setUsernameErrorMessage('Please enter a valid username address.');
      isValid = false;
    } else {
      setUsernameError(false);
      setUsernameErrorMessage('');
    }

    if (!password || password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleRememberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRememberMeChecked(event.target.checked);

    if (!event.target.checked) {
      localStorage.removeItem(KEY.USERNAME);
    }
  };

  return (
    <Card variant="outlined">
      <Typography component="h1" variant="h4" sx={typographyStyles}>
        {t('SignIn')}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={formBoxStyles}>
        <FormControl>
          <FormLabel htmlFor={KEY.USERNAME}>{t('Common_Username')}</FormLabel>
          <TextField
            defaultValue={usernameFieldValue}
            error={usernameError}
            helperText={usernameErrorMessage}
            id={KEY.USERNAME}
            type="text"
            name="username"
            placeholder={t('SignIn_Username_Placeholder')}
            autoComplete="username"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={usernameError ? 'error' : 'primary'}
          />
        </FormControl>
        <FormControl>
          <FormLabel htmlFor={KEY.PASSWORD}>{t('Common_Password')}</FormLabel>
          <TextField
            inputRef={passwordTextFieldRef}
            error={passwordError}
            helperText={passwordErrorMessage}
            name="password"
            placeholder="••••••"
            type="password"
            id={KEY.PASSWORD}
            autoComplete="current-password"
            required
            fullWidth
            variant="outlined"
            color={passwordError ? 'error' : 'primary'}
          />
        </FormControl>
        <FormControlLabel
          control={<Checkbox
            value="remember"
            color="primary"
            checked={isRememberMeChecked}
            onChange={handleRememberChange}
          />}
          label={t('SignIn_RememberMe')}
        />
        <Button type="submit" fullWidth variant="contained" disabled={isLoading}>
          {isLoading ? t('SignIn_Loading') : t('SignIn')}
        </Button>
      </Box>
    </Card>
  );
};

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  '&.MuiPaper-root': { background: '#05070A66 !important', },
  '& .MuiOutlinedInput-root': { background: `${gray[900]} !important`, },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const typographyStyles = {
  width: '100%',
  fontSize: 'clamp(2rem, 10vw, 2.15rem)',
};

const formBoxStyles = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 2,
};

export default SignInCard;
